/**
 * Auth Context - Manages authentication state
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);

  useEffect(() => {
    if (!supabase) {
      console.error('❌ Supabase not configured, auth disabled');
      console.error('Please check REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY environment variables');
      setLoading(false);
      return;
    }

    console.log('✅ Supabase client available, initializing auth...');

    let subscription;
    let timeoutId;

    // Set a timeout to prevent infinite loading
    timeoutId = setTimeout(() => {
      console.warn('⚠️ Auth loading timeout, setting loading to false');
      setLoading(false);
    }, 5000); // 5 second timeout

    // Get initial session
    supabase.auth.getSession()
      .then(({ data: { session }, error }) => {
        if (timeoutId) clearTimeout(timeoutId);
        if (error) {
          console.error('❌ Error getting session:', error);
          console.error('Error details:', {
            message: error.message,
            status: error.status,
            name: error.name
          });
          setLoading(false);
          return;
        }
        console.log('✅ Session retrieved:', session ? `User: ${session.user?.email}` : 'No session');
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      })
      .catch((error) => {
        if (timeoutId) clearTimeout(timeoutId);
        console.error('❌ Error getting session (catch):', error);
        console.error('Error details:', {
          message: error.message,
          stack: error.stack
        });
        setLoading(false);
      });

    // Listen for auth changes
    try {
      const {
        data: { subscription: authSubscription },
      } = supabase.auth.onAuthStateChange((event, session) => {
        if (timeoutId) clearTimeout(timeoutId);
        console.log('🔄 Auth state changed:', event, session?.user?.email || 'No user');
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      });

      subscription = authSubscription;
    } catch (error) {
      console.error('❌ Error setting up auth state listener:', error);
      if (timeoutId) clearTimeout(timeoutId);
      setLoading(false);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, []);

  const signUp = async (email, password, fullName) => {
    if (!supabase) {
      throw new Error('Supabase not configured');
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
        },
      },
    });

    if (error) throw error;
    return data;
  };

  const signIn = async (email, password) => {
    if (!supabase) {
      console.error('❌ Supabase not configured in signIn');
      throw new Error('Supabase not configured');
    }

    console.log('🔐 Attempting sign in with Supabase...');
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      console.error('❌ Sign in error:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.status,
        name: error.name
      });
      throw error;
    }
    
    console.log('✅ Sign in successful:', data.user?.email);
    return data;
  };

  const signInWithOAuth = async (provider) => {
    if (!supabase) {
      throw new Error('Supabase not configured');
    }

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/`,
      },
    });

    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    if (!supabase) {
      throw new Error('Supabase not configured');
    }

    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const getAccessToken = () => {
    return session?.access_token || null;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        signUp,
        signIn,
        signInWithOAuth,
        signOut,
        getAccessToken,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};



